import { useEffect, useState } from "react";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const blankForm = { email: "", password: "", name: "", mobile: "", address: "", pincode: "", is_gst: false, gstin: "", credit_days: 0 };

export const CustomerFormDialog = ({ open, onOpenChange, editing, onCreate, onUpdate }) => {
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing
      ? {
          email: editing.email, password: "", name: editing.name || "", mobile: editing.mobile || "",
          address: editing.address || "", pincode: editing.pincode || "", is_gst: !!editing.is_gst,
          gstin: editing.gstin || "", credit_days: editing.credit_days || 0,
        }
      : blankForm);
  }, [open, editing]);

  const submit = async () => {
    setSaving(true);
    try {
      const base = {
        name: form.name, mobile: form.mobile, address: form.address,
        pincode: form.pincode, is_gst: form.is_gst, gstin: form.is_gst ? form.gstin : "",
        credit_days: parseInt(form.credit_days) || 0,
      };
      if (editing) await onUpdate(editing.id, base);
      else await onCreate({ ...base, email: form.email, password: form.password });
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit customer" : "Add customer"}</DialogTitle>
          <DialogDescription>{editing ? "Update the customer's details." : "Create a customer and set an initial login password."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Email</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-sm mt-1 disabled:opacity-60" data-testid="customer-email"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Mobile</Label>
              <Input value={form.mobile} onChange={(e) => setForm({...form, mobile: e.target.value})} className="rounded-sm mt-1" data-testid="customer-mobile"/>
            </div>
          </div>
          {!editing && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Initial password <span className="text-gray-400 normal-case">(optional)</span></Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder="Leave blank to email a secure set-password link" className="rounded-sm mt-1" data-testid="customer-password"/>
              <p className="text-xs text-gray-400 mt-1">The customer always receives a secure link to set their own password. Set one here only if you need a temporary fallback.</p>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-600">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm mt-1" data-testid="customer-name"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-600">Address</Label>
            <Textarea value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="rounded-sm mt-1" data-testid="customer-address"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Pincode</Label>
              <Input value={form.pincode} maxLength={6} onChange={(e) => setForm({...form, pincode: e.target.value.replace(/\D/g, "")})} className="rounded-sm mt-1" data-testid="customer-pincode"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Credit days</Label>
              <Input type="number" min={0} max={365} value={form.credit_days} onChange={(e) => setForm({...form, credit_days: e.target.value})} className="rounded-sm mt-1" data-testid="customer-credit-days"/>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_gst} onChange={(e) => setForm({...form, is_gst: e.target.checked})} data-testid="customer-is-gst"/>
            GST registered
          </label>
          {form.is_gst && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">GSTIN</Label>
              <Input value={form.gstin} onChange={(e) => setForm({...form, gstin: e.target.value.toUpperCase()})} placeholder="27ABCDE1234F1Z5" className="rounded-sm mt-1 font-mono" data-testid="customer-gstin"/>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#002fa7] hover:bg-[#00227b] rounded-sm" data-testid="customer-save">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
